import { Injectable } from '@angular/core';
import { colors } from 'app/colors.const';
import { ChartOptions, ChartOptions2 } from 'services/models/charts/ChartOptions';
import { FormMetricsBinaryThermometerChartData } from 'services/models/form-builder/form-metrics/chart-data/binary-thermometer-chart-data.model';
import { FormMetricsCategoryChartData } from 'services/models/form-builder/form-metrics/chart-data/category-chart-data.model';
import { FormMetricsTemporalChartData } from 'services/models/form-builder/form-metrics/chart-data/temporal-chart-data.model';
import { FormMetricsThermometerChartData } from 'services/models/form-builder/form-metrics/chart-data/thermometer-chart-data.model';
import { FormMetricsItemModel } from 'services/models/form-builder/form-metrics/form-metrics-item.model';
import { FormMetricsChartType } from 'services/models/form-builder/form-metrics/form-metrics.enum';
import {
  FormMetricsLegendItem,
  USE_CUSTOM_CHART_LEGEND,
  hasAnyLegendIcon,
  resolveLegendDisplay,
} from '../utils/form-metrics-legend.util';

const CHART_PALETTE = [
  colors.solid.primary,
  colors.solid.success,
  colors.solid.warning,
  colors.solid.danger,
  colors.solid.info,
  colors.solid.darkInfo,
  colors.solid.secondary,
  colors.solid.dark,
];

/** Altura mínima e por categoria para barras horizontais legíveis (alinhado ao orgânico: barHeightPerItem=42). */
const BAR_CHART_MIN_HEIGHT = 200;
const BAR_CHART_HEIGHT_PER_CATEGORY = 42;
const BAR_CHART_BASE_PADDING = 80;
/** Espessura da barra — paridade com horizontal-bar-chart (barHeight = '56%'). */
const BAR_CHART_BAR_HEIGHT = '56%';

/** Termômetro 100%: barra mais alta + esconde % em segmentos estreitos (label vai na legenda). */
const THERMOMETER_CHART_HEIGHT = 120;
const THERMOMETER_BAR_HEIGHT = '85%';
const THERMOMETER_LABEL_MIN_PERCENT = 8;

const TOOLTIP_STYLE = {
  fontSize: '13px',
  fontFamily: 'inherit',
};

export interface FormMetricsChartMapResult<T = Partial<ChartOptions>> {
  chartOptions: T;
  /** Preenchido só quando USE_CUSTOM_CHART_LEGEND === true (standby). */
  legendItems: FormMetricsLegendItem[];
}

@Injectable({ providedIn: 'root' })
export class FormMetricsChartMapperService {
  mapPie(item: FormMetricsItemModel): FormMetricsChartMapResult<Partial<ChartOptions2>> {
    const data = item.chartData as FormMetricsCategoryChartData;
    const categories = data?.categories || [];
    const displays = categories.map(c =>
      resolveLegendDisplay({ label: c.label, iconName: c.iconName })
    );

    const chartOptions: Partial<ChartOptions2> = {
      series: categories.map(c => c.count),
      labels: displays.map(d => d.ariaLabel),
      colors: CHART_PALETTE,
      chart: {
        height: 320,
        type: 'pie',
        toolbar: { show: false },
      },
      dataLabels: {
        enabled: true,
        dropShadow: { enabled: true },
      },
      legend: USE_CUSTOM_CHART_LEGEND
        ? { show: false }
        : { position: 'bottom' },
      tooltip: {
        y: {
          formatter: (val: number, opts: any) => {
            const category = categories[opts?.seriesIndex];
            if (!category) {
              return String(val);
            }
            return `${category.label}: ${category.count} (${this.formatPercent(category.percent)}%)`;
          },
        },
        style: TOOLTIP_STYLE,
      },
      noData: { text: 'Sem respostas no período selecionado.' },
    };

    return {
      chartOptions,
      legendItems: USE_CUSTOM_CHART_LEGEND ? this.buildPaletteLegendItems(displays) : [],
    };
  }

  mapBar(item: FormMetricsItemModel): FormMetricsChartMapResult {
    const data = item.chartData as FormMetricsCategoryChartData;
    const categories = data?.categories || [];
    const displays = categories.map(c =>
      resolveLegendDisplay({ label: c.label, iconName: c.iconName })
    );
    const showCustomLegend = USE_CUSTOM_CHART_LEGEND && hasAnyLegendIcon(categories);
    const barChartHeight = Math.max(
      BAR_CHART_MIN_HEIGHT,
      categories.length * BAR_CHART_HEIGHT_PER_CATEGORY + BAR_CHART_BASE_PADDING
    );

    const chartOptions: Partial<ChartOptions> = {
      series: [
        {
          name: 'Respostas',
          data: categories.map(c => c.count),
        },
      ],
      chart: {
        height: barChartHeight,
        type: 'bar',
        toolbar: { show: false },
        zoom: { enabled: false },
      },
      plotOptions: {
        bar: {
          endingShape: 'rounded',
          horizontal: true,
          distributed: true,
          barHeight: BAR_CHART_BAR_HEIGHT,
          dataLabels: {
            hideOverflowingLabels: false,
            position: 'top',
          },
        },
      },
      colors: CHART_PALETTE,
      dataLabels: {
        enabled: true,
        offsetX: -6,
      },
      xaxis: {
        categories: displays.map(d => d.text),
      },
      legend: { show: false },
      tooltip: {
        y: {
          formatter: (val: number, opts: any) => {
            const category = categories[opts?.dataPointIndex];
            if (!category) {
              return String(val);
            }
            return `${category.label}: ${category.count} (${this.formatPercent(category.percent)}%)`;
          },
        },
        style: TOOLTIP_STYLE,
      },
      noData: { text: 'Sem respostas no período selecionado.' },
    };

    return {
      chartOptions,
      legendItems: showCustomLegend ? this.buildPaletteLegendItems(displays) : [],
    };
  }

  mapThermometer(item: FormMetricsItemModel): FormMetricsChartMapResult {
    const data = item.chartData as FormMetricsThermometerChartData;
    const distribution = [...(data?.distribution || [])].sort((a, b) => a.value - b.value);
    const displays = distribution.map(d =>
      resolveLegendDisplay({
        label: d.label,
        iconName: d.iconName,
        valueFallback: d.value,
      })
    );

    const chartOptions: Partial<ChartOptions> = {
      series: distribution.map((d, index) => ({
        name: this.withLegendPercent(displays[index].ariaLabel, d.percent),
        data: [d.count],
      })),
      chart: {
        type: 'bar',
        height: THERMOMETER_CHART_HEIGHT,
        stacked: true,
        stackType: '100%',
        toolbar: { show: false },
      },
      plotOptions: {
        bar: {
          horizontal: true,
          barHeight: THERMOMETER_BAR_HEIGHT,
          dataLabels: { hideOverflowingLabels: true },
        },
      },
      colors: CHART_PALETTE,
      grid: {
        xaxis: { lines: { show: false } },
        yaxis: { lines: { show: false } },
      },
      xaxis: {
        categories: [''],
        labels: { show: false },
      },
      yaxis: { show: false },
      legend: USE_CUSTOM_CHART_LEGEND
        ? { show: false }
        : { position: 'right', horizontalAlign: 'left' },
      fill: { opacity: 1 },
      dataLabels: {
        style: {
          fontSize: '10px',
          colors: [colors.solid.white],
        },
        dropShadow: { enabled: true },
        formatter: (val: number) => this.formatThermometerDataLabel(val),
      },
      tooltip: {
        x: { show: false },
        y: {
          formatter: (val: number, opts: any) => {
            const dist = distribution[opts?.seriesIndex];
            const display = displays[opts?.seriesIndex];
            if (!dist) {
              return String(val);
            }
            return `${display?.ariaLabel || dist.value}: ${dist.count} (${this.formatPercent(
              dist.percent
            )}%)`;
          },
        },
        style: TOOLTIP_STYLE,
      },
      noData: { text: 'Sem respostas no período selecionado.' },
    };

    return {
      chartOptions,
      legendItems: USE_CUSTOM_CHART_LEGEND ? this.buildPaletteLegendItems(displays) : [],
    };
  }

  mapBinaryThermometer(item: FormMetricsItemModel): FormMetricsChartMapResult {
    const data = item.chartData as FormMetricsBinaryThermometerChartData;
    const positiveDisplay = resolveLegendDisplay({
      label: data?.positiveLabel || 'Positivo',
      iconName: data?.positiveIconName,
    });
    const negativeDisplay = resolveLegendDisplay({
      label: data?.negativeLabel || 'Negativo',
      iconName: data?.negativeIconName,
    });

    const chartOptions: Partial<ChartOptions> = {
      series: [
        {
          name: this.withLegendPercent(positiveDisplay.ariaLabel, data?.positivePercent),
          data: [data?.positiveCount || 0],
        },
        {
          name: this.withLegendPercent(negativeDisplay.ariaLabel, data?.negativePercent),
          data: [data?.negativeCount || 0],
        },
      ],
      chart: {
        type: 'bar',
        height: THERMOMETER_CHART_HEIGHT,
        stacked: true,
        stackType: '100%',
        toolbar: { show: false },
      },
      plotOptions: {
        bar: {
          horizontal: true,
          barHeight: THERMOMETER_BAR_HEIGHT,
          dataLabels: { hideOverflowingLabels: true },
        },
      },
      colors: [colors.solid.success, colors.solid.danger],
      grid: {
        xaxis: { lines: { show: false } },
        yaxis: { lines: { show: false } },
      },
      xaxis: {
        categories: [''],
        labels: { show: false },
      },
      yaxis: { show: false },
      legend: USE_CUSTOM_CHART_LEGEND
        ? { show: false }
        : { position: 'right', horizontalAlign: 'left' },
      fill: { opacity: 1 },
      dataLabels: {
        style: {
          fontSize: '10px',
          colors: [colors.solid.white],
        },
        dropShadow: { enabled: true },
        formatter: (val: number) => this.formatThermometerDataLabel(val),
      },
      tooltip: {
        x: { show: false },
        y: {
          formatter: (val: number, opts: any) => {
            if (opts?.seriesIndex === 0) {
              return `${positiveDisplay.ariaLabel}: ${data?.positiveCount || 0} (${this.formatPercent(
                data?.positivePercent
              )}%)`;
            }
            return `${negativeDisplay.ariaLabel}: ${data?.negativeCount || 0} (${this.formatPercent(
              data?.negativePercent
            )}%)`;
          },
        },
        style: TOOLTIP_STYLE,
      },
      noData: { text: 'Sem respostas no período selecionado.' },
    };

    return {
      chartOptions,
      legendItems: USE_CUSTOM_CHART_LEGEND
        ? [
            { color: colors.solid.success, display: positiveDisplay },
            { color: colors.solid.danger, display: negativeDisplay },
          ]
        : [],
    };
  }

  mapTemporal(item: FormMetricsItemModel): Partial<ChartOptions> {
    const data = item.chartData as FormMetricsTemporalChartData;
    const series = data?.series || [];

    return {
      series: [
        {
          name: 'Média',
          data: series.map(s => s.value),
        },
      ],
      chart: {
        height: 320,
        type: 'line',
        zoom: { enabled: false },
        toolbar: { show: false },
      },
      colors: [colors.solid.primary],
      dataLabels: {
        enabled: true,
        dropShadow: { enabled: true },
      },
      stroke: {
        curve: 'straight',
        width: 3,
        lineCap: 'round',
      },
      grid: {
        xaxis: { lines: { show: true } },
      },
      xaxis: {
        categories: series.map(s => s.period),
      },
      tooltip: {
        y: {
          formatter: (val: number, opts: any) => {
            const point = series[opts?.dataPointIndex];
            if (!point) {
              return String(val);
            }
            return `${point.value.toFixed(2)} (${point.count} resp.)`;
          },
        },
        style: TOOLTIP_STYLE,
      },
      noData: { text: 'Sem respostas no período selecionado.' },
    };
  }

  isSupportedChartType(chartType: string): boolean {
    return (
      chartType === FormMetricsChartType.PIZZA ||
      chartType === FormMetricsChartType.BAR ||
      chartType === FormMetricsChartType.THERMOMETER ||
      chartType === FormMetricsChartType.BINARY_THERMOMETER ||
      chartType === FormMetricsChartType.COMMENT ||
      chartType === FormMetricsChartType.TEMPORAL_EVOLUTION
    );
  }

  private buildPaletteLegendItems(
    displays: ReturnType<typeof resolveLegendDisplay>[]
  ): FormMetricsLegendItem[] {
    return displays.map((display, index) => ({
      color: CHART_PALETTE[index % CHART_PALETTE.length],
      display,
    }));
  }

  private formatPercent(value: number | null | undefined): string {
    const percent = Number(value);
    return Number.isFinite(percent) ? percent.toFixed(1) : '0.0';
  }

  private formatThermometerDataLabel(value: number): string {
    const percent = Number(value);
    if (!Number.isFinite(percent) || percent < THERMOMETER_LABEL_MIN_PERCENT) {
      return '';
    }
    return `${percent.toFixed(0)}%`;
  }

  private withLegendPercent(label: string, percent: number | null | undefined): string {
    const rounded = Number(percent);
    return `${label} · ${Number.isFinite(rounded) ? Math.round(rounded) : 0}%`;
  }
}
